import { useState } from 'react';
import {
  Box,
  Divider,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { signOut } from 'next-auth/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';

import { IconButtonAnimate } from '@subflow-frontend/components/animate';
import { MySubscriberAvatar } from '@subflow-frontend/sections/subscriber/MySubscriberAvatar';
import useIsMountedRef from '@subflow-frontend/hooks/useIsMountedRef';
import { useSessionAuth } from '@subflow-frontend/hooks/useSessionAuth';
import {
  PATH_AUTH,
  useSubscriberPaths,
  PATH_SUBSCRIBER,
} from '@subflow-frontend/routes/paths';

export const AccountMenu = ({ session, handleClose }) => {
  const router = useRouter();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const { subscriberPaths } = useSubscriberPaths();

  const handleLogout = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await signOut();

      window?.growsurf && window.growsurf.logout();

      if (isMountedRef.current) {
        handleClose();
      }

      router.push(PATH_AUTH.login);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <Stack sx={{ p: 1 }}>
      <NextLink href={subscriberPaths.dashboard} passHref>
        <MenuItem onClick={handleClose}>Dashboard</MenuItem>
      </NextLink>

      <NextLink href={PATH_SUBSCRIBER.billing.root} passHref>
        <MenuItem onClick={handleClose}>Billing</MenuItem>
      </NextLink>

      <NextLink href={PATH_SUBSCRIBER.profile.root} passHref>
        <MenuItem onClick={handleClose}>Profile</MenuItem>
      </NextLink>

      <Divider />

      <MenuItem onClick={handleLogout} href="/auth/logout">
        Logout
      </MenuItem>
    </Stack>
  );
};

export default function AccountPopover() {
  const { user, session } = useSessionAuth();
  const [open, setOpen] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <Box
        sx={{
          p: 0,
        }}
      >
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={1}
          sx={{
            py: '4px',
            borderRadius: '8px',
          }}
        >
          <NextLink href={PATH_SUBSCRIBER.profile.root} passHref>
            <Stack flexDirection={'row'} sx={{ cursor: 'pointer' }}>
              <MySubscriberAvatar />
              <Stack sx={{ ml: '8px', justifyContent: 'center' }}>
                <Typography variant="subtitle2" color={'#212B36'} noWrap>
                  {user?.name}
                </Typography>
              </Stack>
            </Stack>
          </NextLink>
          <IconButtonAnimate onClick={handleOpen}>
            <KeyboardArrowDownOutlinedIcon sx={{ color: '#5F7993' }} />
          </IconButtonAnimate>
        </Stack>
      </Box>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1.5,
          borderRadius: '8px',
          boxShadow: '-13px 21px 49px -31px rgba(0, 0, 0, 0.13)',
        }}
        onClose={handleClose}
      >
        <AccountMenu session={session} handleClose={handleClose} />
      </Popover>
    </>
  );
}
